import React, { useMemo } from 'react';
import { Award, formatMonthYear } from '@personal-portfolio/contract';
import { useSiteContent } from '@personal-portfolio/queries';
import {
  Trophy,
  Calendar,
  FileText,
  Download,
  ExternalLink,
} from 'lucide-react';
import { LinkButton } from '@/shared/ui/button';
import { useFirebase } from '@/services/analytics/use-firebase';

interface AwardCardProps {
  award: Award;
}

export const AwardCard: React.FC<AwardCardProps> = ({ award }) => {
  const { cards } = useSiteContent();
  const { trackCertificateView, trackCertificateDownload } = useFirebase();

  const certificateLink = useMemo(() => award.certificate?.url, [award]);

  const getAwardLinks = (award: Award) => {
    return award.links || [];
  };

  const getFileExtension = (url: string) => {
    return url.split('.').pop()?.toLowerCase();
  };

  // Extension sniffing, not a content-type check: the certificate is a Strapi
  // media URL and no MIME type reaches the client here. It only decides
  // view-vs-download affordance, so a wrong guess is cosmetic.
  const isImageFile = (url: string) => {
    const ext = getFileExtension(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '');
  };

  const handleCertificateClick = (award: { title: string }, url: string) => {
    const isImage = isImageFile(url);

    if (isImage) {
      trackCertificateView(award.title);
    } else {
      trackCertificateDownload(award.title);
    }
  };

  return (
    <div id={award.slug} className="flex w-full scroll-mt-20 items-start gap-5">
      <div className="mt-5 flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg border bg-muted text-foreground">
        <Trophy size={22} />
      </div>

      <div className="flex-1 rounded-xl border border-border bg-card p-4 shadow-xs transition-shadow duration-200 hover:shadow-md">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
            <Calendar size={14} />
            <span>{formatMonthYear(award.date)}</span>
          </div>

          {certificateLink && (
            <LinkButton
              variant="outline"
              size="sm"
              href={certificateLink}
              target="_blank"
              rel="noopener noreferrer"
              onPress={() => handleCertificateClick(award, certificateLink)}
            >
              {isImageFile(certificateLink) ? (
                <FileText size={12} />
              ) : (
                <Download size={12} />
              )}
              {isImageFile(certificateLink) ? cards.view : cards.download}
            </LinkButton>
          )}
        </div>

        <h2 className="mb-2 text-lg leading-snug font-semibold tracking-tight text-card-foreground">
          {award.title}
        </h2>

        {award.description && (
          <p className="mb-3 text-sm leading-relaxed break-words text-muted-foreground">
            {award.description}
          </p>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{cards.issuedBy}:</span>
          <span className="font-medium">{award.issuer}</span>
        </div>

        {getAwardLinks(award).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-4">
            {getAwardLinks(award).map((link) => (
              <LinkButton
                key={link.id}
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
                <ExternalLink className="ml-1" />
              </LinkButton>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
